/* eslint-disable @next/next/no-img-element */
'use client'
import * as React from 'react'
import Image from 'next/image'
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion'
import { tw } from 'utils/classnames'
import { SendIcon, SpinnerIcon } from 'primitives/icons'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { useSocialCommentPostMutation } from '@web/gen/feed'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { PhotoPlusIcon, EmojiPlusIcon } from '@repo/web-ui/ask-cf/components/icons'
import { Button } from '@repo/design-system-cf/components/button'
import { ImageUpload } from './image-upload'
import type { SocialComment } from 'codegen/types'
import { SocialAssetType } from 'codegen/types'
import type { Project } from '../page-client'
import type { UploadedFile } from '../_hooks/use-image-upload'
import * as Popover from 'primitives/popover'
import type { EmojiClickData } from 'emoji-picker-react'
import EmojiPicker from 'emoji-picker-react'

export function ProjectCommentInput({
  projectData,
  onSubmit,
  parentComment,
  open,
  setOpen,
  focus,
  hideAvatar = false,
}: {
  projectData: Project
  onSubmit?: (newComment: SocialComment) => void
  parentComment?: SocialComment
  open: boolean
  setOpen?: (open: boolean) => void
  focus?: number
  hideAvatar?: boolean
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)
  const isInitialMount = React.useRef(true)
  const initialOpen = React.useRef(open)

  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { data: userData } = useUser()
  const { openModal } = useUserAuthModal()

  const [text, setText] = React.useState<string>('')
  const [error, setError] = React.useState<string>('')

  const [images, setImages] = React.useState<UploadedFile[]>([])
  const [isImageUploading, setIsImageUploading] = React.useState<boolean>(false)
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = React.useState(false)

  const imageUploadRef = React.useRef<HTMLInputElement>(null)
  const { mutate: socialCommentPostMutation, isPending: isSubmitting } =
    useSocialCommentPostMutation()

  // oxlint-disable-next-line require-await
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    e.preventDefault() // ⛔ no refresh
    if (!isLoggedIn) {
      openModal('register')
      return
    }

    if (!text.trim() || isImageUploading || isSubmitting) {
      return
    }

    socialCommentPostMutation(
      {
        input: {
          postId: projectData.id,
          content: text.trim(),
          assets: images.map(image => ({
            type: SocialAssetType.Image,
            url: image.uploadedUrl,
          })),
          ...(parentComment?.id && { commentParentId: parentComment.id }),
        },
      },
      {
        onSuccess: data => {
          const result = data?.socialCreateComment
          const newComment = result?.comment
          if (result?.success && newComment) {
            onSubmit?.(newComment as SocialComment)
          }
          setOpen?.(false)
          setText('')
          setImages([])
          setError('')
        },
      },
    )
  }

  //files
  const handleFileSelect = (files: UploadedFile[]) => {
    setImages(prev => [...prev, ...files])
  }

  const triggerImageUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!isLoggedIn) {
      openModal('register')
      return
    }
    imageUploadRef.current?.click()
  }

  const resizeTextarea = () => {
    if (!textareaRef.current) {
      return
    }
    textareaRef.current.style.height = `36px`
    // Get real scroll height
    const scrollHeight = textareaRef.current.scrollHeight
    // Only grow when content exceeds first line
    if (scrollHeight > 36) {
      textareaRef.current.style.height = scrollHeight + 'px'
    }
  }

  const blockIfLoggedOut = (e: React.SyntheticEvent) => {
    if (!isLoggedIn) {
      e.preventDefault()
      e.stopPropagation?.()
      openModal('register')
      textareaRef.current?.blur()
      return true
    }
    return false
  }

  React.useEffect(() => {
    resizeTextarea()
  }, [text])

  React.useEffect(() => {
    if (focus) {
      textareaRef.current?.focus()
    }
  }, [focus])

  React.useEffect(() => {
    isInitialMount.current = false
  }, [])

  // React.useEffect(() => {
  //   if (open) {
  //     textareaRef.current?.focus()
  //   }
  // }, [open])

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const newText = text + emojiData.emoji + ''
    setText(newText)
    textareaRef.current?.focus()
  }

  const handleEmojiButtonClick = () => {
    if (!isLoggedIn) {
      openModal('register')
      return
    }
    setIsEmojiPickerOpen(true)
  }

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {open ? (
          <m.div
            key="input"
            initial={
              isInitialMount.current && initialOpen.current ? false : { opacity: 0, height: 0 }
            }
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="w-full"
            data-testid="project-comment-input-container"
          >
            <div className="flex flex-row gap-x-2 w-full">
              <div className={tw(images.length > 0 ? 'flex overflow-hidden mb-2' : 'hidden')}>
                <ImageUpload
                  className="w-full"
                  images={images}
                  onFileAdd={handleFileSelect}
                  onFileDelete={index => setImages(prev => prev.filter((_, i) => i !== index))}
                  maxImages={1}
                  onFilesReordered={setImages}
                  inputRef={imageUploadRef}
                  setLoading={setIsImageUploading}
                />
              </div>
            </div>
            <div className="flex flex-row gap-x-2 items-center">
              <form
                onSubmit={handleSubmit}
                className={tw(
                  'w-full flex flex-row bg-cf-white justify-center items-end gap-x-1 px-2 py-1.5 rounded-2 border ',
                  error
                    ? 'border-cf-red-900'
                    : 'border-[#1313151F] hover:border-[#13131533] active:border-[#13131585]',
                  text.trim() === '' ? null : 'shadow-[0_0_4px_0_rgba(0,0,0,0.25)]',
                )}
              >
                <div className="flex-1 flex justify-start items-center">
                  {isLoggedIn && !hideAvatar ? (
                    <Image
                      src={userData?.avatarUrl || `https://avatar.iran.liara.run/public/${1}`}
                      alt="logo"
                      width={36}
                      height={36}
                      className="overflow-hidden size-9 rounded-full tablet:block hidden shrink-0"
                    />
                  ) : null}
                  <textarea
                    value={text}
                    ref={textareaRef}
                    className={tw(
                      'flex-1 ml-2 w-full h-9 overflow-hidden resize-none py-2 text-[14px] leading-[20px] focus:ring-0 focus:outline-none placeholder:text-cf-grey-500',
                    )}
                    placeholder="Add a reply"
                    onPointerDown={blockIfLoggedOut}
                    onFocus={blockIfLoggedOut}
                    onTouchStart={blockIfLoggedOut}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault() // stop form from triggering file input
                        handleSubmit(e)
                      }
                    }}
                    data-testid="project-comment-input-textarea"
                  />
                </div>
                <Popover.Root open={isEmojiPickerOpen} onOpenChange={setIsEmojiPickerOpen}>
                  <Popover.Trigger asChild>
                    <Button
                      variant="ghost"
                      size="small"
                      className="!h-7 my-1 hidden tablet:block"
                      iconLeft={<EmojiPlusIcon className="text-cf-purple-900 !size-4" />}
                      onClick={handleEmojiButtonClick}
                      data-testid="project-comment-input-emoji-button"
                      aria-label="Add emoji"
                    />
                  </Popover.Trigger>
                  <Popover.Content className="z-50">
                    <EmojiPicker
                      onEmojiClick={handleEmojiClick}
                      skinTonesDisabled={true}
                      allowExpandReactions={false}
                      searchDisabled={true}
                      previewConfig={{
                        showPreview: false,
                      }}
                    />
                  </Popover.Content>
                </Popover.Root>
                <Button
                  variant={'ghost'}
                  size="small"
                  className="!h-7 my-1"
                  iconLeft={<PhotoPlusIcon className="text-cf-purple-900 p-0.5" />}
                  onClick={triggerImageUpload}
                  data-testid="project-comment-input-image-upload-button"
                  aria-label="Upload image"
                />
                <Button
                  disabled={isImageUploading || isSubmitting || text.trim() === ''}
                  type="submit"
                  variant={'primary'}
                  className="!h-9 ml-1"
                  iconLeft={
                    isImageUploading || isSubmitting ? (
                      <SpinnerIcon className="animate-spin" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  data-testid="project-comment-input-submit-button"
                  aria-label="Send comment"
                />
              </form>
            </div>
          </m.div>
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  )
}
